import React, { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Clock, Users, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/auth';
import { ShiftService } from '@/services/shiftService';
import { ShiftAssignModalProps, Shift } from '@/types/shifts';
import { SHIFT_LABELS } from '@/constants/ui';
import { ShiftSelector } from '@/components/common/ShiftSelector';
import { formatDayGroupsForDisplay } from '@/utils/shiftFormUtils';

/**
 * Shift selection form with category and shift dropdowns
 */
const ShiftSelectionForm = memo<{
  onShiftSelect: (shift: Shift | null) => void;
  selectedShift: Shift | null;
}>(({ onShiftSelect, selectedShift }) => {
  const { user } = useAuth();
  const [allShifts, setAllShifts] = useState<Shift[]>([]);
  const [selectedShiftId, setSelectedShiftId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch shifts for the company
  useEffect(() => {
    const fetchShifts = async () => {
      if (!user?.companyId) return;

      try {
        setIsLoading(true);
        setError(null);
        
        const shifts = await ShiftService.fetchShifts(user.companyId);
        // Filter shifts to show only those with in_working_hours = FALSE
        const filteredShifts = shifts.filter(shift => shift.in_working_hours === false);
        setAllShifts(filteredShifts);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load shifts';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchShifts();
  }, [user?.companyId]);

  // Handle shift selection - simplified 
  const handleShiftSelect = useCallback((shiftId: string) => {
    setSelectedShiftId(shiftId);
    const shift = allShifts.find(s => s.id === shiftId) || null;
    onShiftSelect(shift);
  }, [allShifts, onShiftSelect]);

  // Get selected shift details
  const selectedShiftDetails = useMemo(() => {
    if (selectedShift) return selectedShift;
    if (selectedShiftId) {
      return allShifts.find(s => s.id === selectedShiftId) || null;
    }
    return null;
  }, [selectedShift, selectedShiftId, allShifts]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Category and Shift Selection Skeleton - Only the dropdown fields */}
        <div className="grid grid-cols-2 gap-3">
          {/* Category Selection Skeleton */}
          <div className="space-y-1.5">
            <div className="h-3 w-16 bg-muted rounded animate-pulse"></div>
            <div className="h-8 bg-muted rounded animate-pulse"></div>
          </div>
          
          {/* Shift Selection Skeleton */}
          <div className="space-y-1.5">
            <div className="h-3 w-20 bg-muted rounded animate-pulse"></div>
            <div className="h-8 bg-muted rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <Clock className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <h3 className="text-base font-semibold mb-2">Error loading shifts</h3>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* All Fields in Consistent Grid Layout */}
      <div className="space-y-3">
        {/* Unified Shift Selection */}
        <ShiftSelector
          shifts={allShifts}
          selectedShiftId={selectedShiftId}
          onShiftChange={handleShiftSelect}
          label="Select Shift"
          placeholder="Select a shift to assign"
          compact={true}
        />

        {/* Schedule Display - Below in its own row */}
        {selectedShiftDetails && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Schedule</label>
            <div className="h-8 flex items-center px-3 bg-muted rounded-md border border-input">
              {selectedShiftDetails.day_time_deduction && 
               Array.isArray(selectedShiftDetails.day_time_deduction) && 
               selectedShiftDetails.day_time_deduction.length > 0 ? (
                <div className="flex items-center space-x-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <div className="text-xs text-slate-700 dark:text-slate-300">
                    {formatDayGroupsForDisplay(selectedShiftDetails.day_time_deduction).map((schedule, index, array) => (
                      <span key={index}>
                        {schedule}{index < array.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground italic">No schedule configured</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * Modal for assigning shifts to selected employees
 */
export const ShiftAssignModal = memo<ShiftAssignModalProps>(({
  isOpen,
  onClose,
  onUpdate,
  selectedEmployeeCount,
  isUpdating,
  selectedShift,
  onShiftSelect
}) => {
  const handleUpdate = useCallback(async () => {
    if (!selectedShift) {
      return;
    }
    await onUpdate(selectedShift.id);
  }, [selectedShift, onUpdate]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[75vh] overflow-y-auto">
        <DialogHeader className="pb-3">
          <DialogTitle className="flex items-center justify-between text-base pr-8">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4" />
              <span className="font-bold">{SHIFT_LABELS.SHIFT_ASSIGNMENT_MODAL_TITLE}</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              <span>{selectedEmployeeCount} employee{selectedEmployeeCount !== 1 ? 's' : ''} selected</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Shift Selection Form */}
          <ShiftSelectionForm
            onShiftSelect={onShiftSelect}
            selectedShift={selectedShift}
          />

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-3 border-t">
            {/* Warning Message - Left side */}
            <div className="flex items-center space-x-2 text-xs text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
              <span>This will replace current shifts for selected employees.</span>
            </div>
            
            {/* Action Buttons - Right side */}
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={isUpdating}
                className="h-8 px-3"
              >
                {SHIFT_LABELS.CANCEL_BUTTON}
              </Button>
              <Button
                size="sm"
                onClick={handleUpdate}
                disabled={!selectedShift || isUpdating}
                className="h-8 px-3 min-w-[100px]"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                    {SHIFT_LABELS.UPDATING_TEXT}
                  </>
                ) : (
                  SHIFT_LABELS.UPDATE_SHIFTS_BUTTON
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

ShiftAssignModal.displayName = 'ShiftAssignModal'; 